import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, CalendarCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+()\-.\s\d]{7,20}$/;

const CARE_FOR_OPTIONS = ["Myself", "My parent", "My spouse / partner", "Another family member", "A client / patient", "Other"];
const TIME_OPTIONS = ["Morning (9 AM – 12 PM)", "Afternoon (12 PM – 4 PM)", "Evening (4 PM – 7 PM)", "Flexible / Any time"];

const initialForm = { name: "", phone: "", email: "", care_for: "", preferred_date: "", preferred_time: "", message: "" };

const Field = ({ label, name, required, error, children }) => (
  <div>
    <label htmlFor={name} className="block text-sm font-bold text-brand-navy">
      {label} {required && <span className="text-brand-teal" aria-hidden="true">*</span>}
    </label>
    <div className="mt-2">{children}</div>
    {error && <p data-testid={`error-${name}`} className="mt-1.5 text-xs font-semibold text-red-700" role="alert">{error}</p>}
  </div>
);

const inputCls = (err) =>
  `w-full rounded-xl border ${err ? "border-red-400 bg-red-50/40" : "border-slate-300 bg-white"} px-4 py-3 text-base text-brand-navy placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-teal/60 focus:border-brand-teal transition-colors duration-300`;

export const ConsultationForm = () => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((e) => ({ ...e, [name]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Full name is required.";
    if (!form.phone.trim()) e.phone = "Phone number is required.";
    else if (!PHONE_RE.test(form.phone.trim())) e.phone = "Please enter a valid phone number.";
    if (!form.email.trim()) e.email = "Email address is required.";
    else if (!EMAIL_RE.test(form.email.trim())) e.email = "Please enter a valid email address.";
    if (!form.preferred_date) e.preferred_date = "Please choose a preferred date.";
    if (!form.preferred_time) e.preferred_time = "Please choose a preferred time.";
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) {
      toast.error("Please complete the required fields highlighted below.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/consultations`, form);
      setSubmitted(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again or call us at +1 (778) 837-2786.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="consultation-confirmation" className="rounded-2xl border border-brand-teal/30 bg-white p-10 text-center shadow-[0_16px_50px_rgb(0,128,128,0.10)]">
        <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-brand-teal/10 text-brand-teal">
          <CheckCircle2 className="h-8 w-8" strokeWidth={1.5} />
        </span>
        <h3 className="mt-6 font-display text-2xl md:text-3xl text-brand-navy">Thank You</h3>
        <p className="mt-4 text-base leading-relaxed text-slate-600">
          Thank you. Your consultation request has been received. A member of BMS Health Care Services will contact you shortly to confirm your free care consultation. A confirmation email has also been sent to your email address.
        </p>
      </div>
    );
  }

  const today = new Date().toISOString().split("T")[0];

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      data-testid="consultation-form"
      className="rounded-2xl border border-slate-200 bg-white p-6 md:p-10 shadow-[0_16px_50px_rgb(11,27,61,0.08)] space-y-5"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Full Name" name="name" required error={errors.name}>
          <input id="name" data-testid="input-consultation-name" type="text" autoComplete="name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls(errors.name)} />
        </Field>
        <Field label="Phone Number" name="phone" required error={errors.phone}>
          <input id="phone" data-testid="input-consultation-phone" type="tel" autoComplete="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls(errors.phone)} />
        </Field>
      </div>
      <Field label="Email Address" name="email" required error={errors.email}>
        <input id="email" data-testid="input-consultation-email" type="email" autoComplete="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls(errors.email)} />
      </Field>
      <Field label="Who is the care for?" name="care_for" error={errors.care_for}>
        <select id="care_for" data-testid="input-consultation-care-for" value={form.care_for} onChange={(e) => set("care_for", e.target.value)} className={inputCls(errors.care_for)}>
          <option value="">Select an option (optional)</option>
          {CARE_FOR_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Preferred Date" name="preferred_date" required error={errors.preferred_date}>
          <input id="preferred_date" data-testid="input-consultation-date" type="date" min={today} value={form.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} className={inputCls(errors.preferred_date)} />
        </Field>
        <Field label="Preferred Time" name="preferred_time" required error={errors.preferred_time}>
          <select id="preferred_time" data-testid="input-consultation-time" value={form.preferred_time} onChange={(e) => set("preferred_time", e.target.value)} className={inputCls(errors.preferred_time)}>
            <option value="">Select a time</option>
            {TIME_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Anything you'd like us to know?" name="message" error={errors.message}>
        <textarea id="message" data-testid="input-consultation-message" rows={4} value={form.message} onChange={(e) => set("message", e.target.value)} className={inputCls(errors.message)} placeholder="Optional — share any questions or details about the care needed." />
      </Field>
      <p className="text-xs leading-relaxed text-slate-500">
        Your information is kept confidential and used only to arrange your consultation.
      </p>
      <button
        type="submit"
        data-testid="submit-consultation-button"
        disabled={submitting}
        className="w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-brand-teal px-7 py-4 text-base md:text-lg font-bold text-white shadow-[0_12px_36px_rgba(0,128,128,0.35)] hover:bg-brand-navy transition-colors duration-300 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <CalendarCheck className="h-5 w-5" strokeWidth={1.8} />
        {submitting ? "Submitting…" : "Request Free Consultation"}
      </button>
    </form>
  );
};
